import {
  AnyAction,
  createSlice,
  Dispatch,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  GeoLocResponse,
  ILocationServices,
  ILocationServicesError,
  ISelectedMarker,
  IStreetlightData,
  IUserLocation,
} from "../../../components/map/Map.types";
import { ILogMessage } from "../../../shared/services/Logging.service";
import { AppDispatch, AppThunk } from "../../store";
import { DispatchLogger } from "../../utils/dispatch-logger/DispatchLogger";
import { ELogTypes } from "../../../shared/enums/logging.enums";
import { IAppConfig } from "../../../shared/types/config.types";
import configAPI from "../../../api/config.api";
import { AxiosError } from "axios";
import { fetchLookups } from "../lookups/lookups.slice";
import { ApiService } from "../../../api/api.service";
import { EBracketTypes } from "../../../shared/enums/utility.enums";
import { ESearchWizardSteps } from "../../../shared/enums/search.enums";
import {
  OutagesIdResponse,
  OutageViewModel,
} from "../../../api/generated/models";
import { AuthState } from "@okta/okta-auth-js";
import { AuthenticationResult } from "@azure/msal-browser";

export interface IAuthState {
  loggedIn: boolean;
  oktaAuthState: AuthState | null;
  entraAuthState: AuthenticationResult | null;
}

export interface IRoutingState {
  outageId?: string | null;
  selectedOutage?: OutageViewModel | null;
  routeNavigationComplete: boolean;
  routingRoutineComplete: boolean;
  outageNotFound: boolean;
  originatorFlag: string | null;
}

export interface IPinModeState {
  enabled: boolean;
  pin: ISelectedMarker;
  selectedBracketType: string | null;
}

export interface ISearchModeState {
  enabled: boolean;
  searchText: string | null;
  activeView: "search" | "results" | "detail";
}

export interface IAdminModeState {
  enabled: boolean;
  selectedOutage: (OutageViewModel & { contacts?: Array<any> }) | null;
  showAdminModal: boolean;
}

export interface IUIState {
  baseMapInitialized: boolean;
  featureLayerInitialized: boolean;
  restrictedLayerInitialized: boolean;
  grandRapidsLayerInitialized: boolean;
  saginawLayerInitialized: boolean;
  jacksonLayerInitialized: boolean;
  isDrawerOpen: boolean;
  selectedMarker: ISelectedMarker;
  loading: boolean;
  routing: IRoutingState;
  pinMode: IPinModeState;
  searchMode: ISearchModeState;
  adminMode: IAdminModeState;
}

export interface IGlobalState {
  appConfig: IAppConfig | null;
  auth: IAuthState;
  locationServices: ILocationServices;
  logs: Array<ILogMessage>;
  ui: IUIState;
}

/**
 * --------------------------------------------------------------------------
 *  INITIAL STATE
 * --------------------------------------------------------------------------
 */
const initialState: IGlobalState = {
  appConfig: null,
  auth: {
    loggedIn: false,
    oktaAuthState: null,
    entraAuthState: null,
  },
  locationServices: {
    locationDisabled: false,
    userLocation: null,
    locationServicesError: null,
  },
  logs: [],
  ui: {
    baseMapInitialized: false,
    featureLayerInitialized: false,
    restrictedLayerInitialized: false,
    grandRapidsLayerInitialized: false,
    saginawLayerInitialized: false,
    jacksonLayerInitialized: false,
    isDrawerOpen: false,
    loading: false,
    selectedMarker: {
      geoLocation: null,
      streetLightData: null,
    },
    routing: {
      outageId: null,
      selectedOutage: null,
      routeNavigationComplete: false,
      routingRoutineComplete: false,
      outageNotFound: false,
      originatorFlag: null,
    },
    pinMode: {
      enabled: false,
      pin: {
        geoLocation: null,
        streetLightData: null,
      },
      selectedBracketType: null,
    },
    searchMode: {
      enabled: false,
      searchText: null,
      activeView: ESearchWizardSteps.SEARCH,
    },
    adminMode: {
      enabled: false,
      selectedOutage: null,
      showAdminModal: false,
    },
  },
};

/**
 * --------------------------------------------------------------------------
 *  STATE SLICE
 * --------------------------------------------------------------------------
 */
const globalStateSlice = createSlice({
  name: "globalState",
  initialState,
  reducers: {
    setDrawerState(state, action: PayloadAction<{ isDrawerOpen: boolean }>) {
      const { isDrawerOpen } = action.payload;
      state.ui.isDrawerOpen = isDrawerOpen;
    },
    getConfigSuccess(state, action: PayloadAction<IAppConfig | any>) {
      if (action.payload.results) {
        state.appConfig = action.payload.results;
      } else {
        state.appConfig = action.payload;
      }
    },
    getConfigFailure(state, action: PayloadAction<AxiosError<IAppConfig>>) {
      state.appConfig = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.ui.loading = action.payload;
    },
    setLoggedIn(state, action: PayloadAction<boolean>) {
      state.auth.loggedIn = action.payload;
    },
    setOktaAuthState(state, action: PayloadAction<AuthState | null>) {
      state.auth.oktaAuthState = action.payload;
    },
    setEntraAuthstate(
      state,
      action: PayloadAction<AuthenticationResult | null>
    ) {
      state.auth.entraAuthState = action.payload;
    },
    setBaseMapInitialized(state, action: PayloadAction<boolean>) {
      state.ui.baseMapInitialized = action.payload;
    },
    setFeatureLayerInitialized(state, action: PayloadAction<boolean>) {
      state.ui.featureLayerInitialized = action.payload;
    },
    setRestrictedLayerInitialized(state, action: PayloadAction<boolean>) {
      state.ui.restrictedLayerInitialized = action.payload;
    },
    setGrandRapidsLayerInitialized(state, action: PayloadAction<boolean>) {
      state.ui.grandRapidsLayerInitialized = action.payload;
    },
    setSaginawLayerInitialized(state, action: PayloadAction<boolean>) {
      state.ui.saginawLayerInitialized = action.payload;
    },
    setJacksonLayerInitialized(state, action: PayloadAction<boolean>) {
      state.ui.jacksonLayerInitialized = action.payload;
    },
    setRoutingOutageId(state, action: PayloadAction<string>) {
      state.ui.routing.outageId = action.payload;
    },
    setRoutingOutage(state, action: PayloadAction<OutagesIdResponse>) {
      state.ui.routing.selectedOutage = action.payload;
      state.ui.routing.routeNavigationComplete = true;
    },
    setOutageNotFound(state, action: PayloadAction<boolean>) {
      state.ui.routing.outageNotFound = action.payload;
      state.ui.routing.routeNavigationComplete = true;
    },
    setRoutingRoutineComplete(state, action: PayloadAction<boolean>) {
      state.ui.routing.routingRoutineComplete = true;
    },
    setOriginatorFlag(state, action: PayloadAction<string>) {
      state.ui.routing.originatorFlag = action.payload;
    },
    setUserLocation(
      state,
      action: PayloadAction<{ userLocation: IUserLocation | null }>
    ) {
      const { userLocation } = action.payload;
      state.locationServices.userLocation = userLocation;
      state.locationServices.locationServicesError = null;
      state.locationServices.locationDisabled = false;
    },
    setUserLocationError(
      state,
      action: PayloadAction<{ error: ILocationServicesError }>
    ) {
      const { error } = action.payload;
      state.locationServices.userLocation = null;
      state.locationServices.locationServicesError = error;
      state.locationServices.locationDisabled = true;
    },
    setSelectedMarkerGeoLoc(
      state,
      action: PayloadAction<{ selectedMarker: any }>
    ) {
      const { selectedMarker } = action.payload;
      state.ui.selectedMarker.geoLocation = selectedMarker;
    },
    setSelectedMarkerData(
      state,
      action: PayloadAction<{ streetlightData: IStreetlightData }>
    ) {
      const { streetlightData } = action.payload;
      state.ui.selectedMarker.streetLightData = streetlightData;
    },
    setSelectedOutageAdminMode(
      state,
      action: PayloadAction<{ selectedOutage: OutageViewModel }>
    ) {
      state.ui.adminMode.selectedOutage = action.payload.selectedOutage;
    },
    setSearchMode(state, action: PayloadAction<{ enabled: boolean }>) {
      const { enabled } = action.payload;
      state.ui.searchMode.enabled = enabled;
      state.ui.isDrawerOpen = enabled;
    },
    setSearchView(
      state,
      action: PayloadAction<{ activeView: "search" | "results" | "detail" }>
    ) {
      const { activeView } = action.payload;
      state.ui.searchMode.activeView = activeView;
    },
    setSearchTerm(state, action: PayloadAction<string>) {
      state.ui.searchMode.searchText = action.payload;
    },
    setShowAdminModal(state, action: PayloadAction<{ show: boolean }>) {
      const { show } = action.payload;
      state.ui.adminMode.showAdminModal = show;
    },
    setAdminMode(state, action: PayloadAction<{ enabled: boolean }>) {
      const { enabled } = action.payload;
      state.ui.adminMode.enabled = enabled;
    },
    setPinMode(state, action: PayloadAction<{ enabled: boolean }>) {
      const { enabled } = action.payload;
      state.ui.pinMode.enabled = enabled;
    },
    setPin(
      state,
      action: PayloadAction<{
        geoLocation: any;
        selectedMarkerData: IStreetlightData;
      }>
    ) {
      const { geoLocation, selectedMarkerData } = action.payload;
      state.ui.pinMode.pin.geoLocation = geoLocation;
      state.ui.pinMode.pin.streetLightData = selectedMarkerData;
    },
    setPinBracketType(
      state,
      action: PayloadAction<{
        bracketType: EBracketTypes | null;
      }>
    ) {
      const { bracketType } = action.payload;
      state.ui.pinMode.selectedBracketType = bracketType;
    },
    updateLogs(state, action: PayloadAction<ILogMessage>) {
      state.logs.push(action.payload);
    },
    clearLocationError(state) {
      state.locationServices.locationServicesError = null;
    },
    clearSearchTerm(state) {
      state.ui.searchMode.searchText = null;
    },
    clearSelectedMarker(state) {
      state.ui.selectedMarker = {
        geoLocation: null,
        streetLightData: null,
      };
    },
  },
});

/**
 * --------------------------------------------------------------------------
 *  ACTIONS
 * --------------------------------------------------------------------------
 */
export const {
  setDrawerState,
  setUserLocation,
  setUserLocationError,
  getConfigSuccess,
  getConfigFailure,
  updateLogs,
  setOutageNotFound,
  setLoading,
  setLoggedIn,
  setOktaAuthState,
  setEntraAuthstate,
  setBaseMapInitialized,
  setFeatureLayerInitialized,
  setRestrictedLayerInitialized,
  setGrandRapidsLayerInitialized,
  setSaginawLayerInitialized,
  setJacksonLayerInitialized,
  setRoutingOutageId,
  setRoutingOutage,
  setRoutingRoutineComplete,
  setOriginatorFlag,
  setSelectedMarkerGeoLoc,
  setSelectedMarkerData,
  setSelectedOutageAdminMode,
  setAdminMode,
  setShowAdminModal,
  setPinMode,
  setPin,
  setSearchMode,
  setSearchView,
  setSearchTerm,
  setPinBracketType,
  clearLocationError,
  clearSearchTerm,
  clearSelectedMarker,
} = globalStateSlice.actions;

/**
 * --------------------------------------------------------------------------
 *  THUNKS
 *  @todo: Create seperate file for these
 * --------------------------------------------------------------------------
 */
export const fetchAppConfig =
  (): any => async (dispatch: Dispatch<AnyAction>, getState: any) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    try {
      const results: any = await configAPI.getConfig();

      if (results.env) {
        const apiService: ApiService = ApiService.getInstance();
        apiService.appConfig = results;
        apiService.initializeAPIs();

        dispatchLogger.fire(
          getConfigSuccess,
          { results },
          {
            message: "App Config Fetched.",
            type: ELogTypes.API,
            data: { results },
          }
        );

        dispatch(fetchLookups());
      } else {
        dispatchLogger.fire(
          getConfigFailure,
          { results },
          {
            message: "App Config Failed.",
            type: ELogTypes.ERROR,
            data: { results },
          }
        );
        dispatch(getConfigFailure(results));
      }
    } catch (error) {
      dispatchLogger.fire(
        getConfigFailure,
        { error },
        {
          message: "App Config Failed.",
          type: ELogTypes.ERROR,
          data: { error },
        }
      );
    }
  };

export const updateUserLocation =
  (userLocation: IUserLocation): any =>
  (dispatch: Dispatch<AnyAction>) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setUserLocation,
      { userLocation },
      {
        message: "User location updated.",
        type: ELogTypes.GEO_LOC_EVENT,
        data: { userLocation },
      }
    );
  };

export const updateUserLocationError =
  (error: ILocationServicesError): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setUserLocationError,
      { error },
      {
        message: error.message || "",
        type: ELogTypes.ERROR,
        data: { error },
      }
    );
  };

export const updateMarkerGeoLoc =
  (selectedMarker: GeoLocResponse): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setSelectedMarkerGeoLoc,
      { selectedMarker },
      {
        message: "Selected Marker Geo Location Updated.",
        type: ELogTypes.GEO_LOC_EVENT,
        data: { selectedMarker },
      }
    );
  };

export const updateMarkerData =
  (streetlightData: IStreetlightData): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setSelectedMarkerData,
      { streetlightData },
      {
        message: "Selected Marker Data Updated.",
        type: ELogTypes.MAP_CLICK,
        data: { streetlightData },
      }
    );
  };

export const updateDrawerState =
  (isDrawerOpen: boolean): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setDrawerState,
      { isDrawerOpen },
      {
        message: "Drawer State Changed",
        type: ELogTypes.STATE_CHANGED,
        data: { isDrawerOpen },
      }
    );
  };

export const updateSearchModeState =
  (enabled: boolean): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setSearchMode,
      { enabled },
      {
        message: "Search Mode Changed",
        type: ELogTypes.STATE_CHANGED,
        data: { enabled },
      }
    );
  };

export const updatePinModeState =
  (enabled: boolean): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setPinMode,
      { enabled },
      {
        message: "Pin Mode Changed",
        type: ELogTypes.STATE_CHANGED,
        data: { enabled },
      }
    );
  };

export const updateAdminModeState =
  (enabled: boolean): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setAdminMode,
      { enabled },
      {
        message: "Admin Mode Changed",
        type: ELogTypes.STATE_CHANGED,
        data: { enabled },
      }
    );
  };

export const updatePin =
  (geoLocation: any, selectedMarkerData: IStreetlightData): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setPin,
      { geoLocation, selectedMarkerData },
      {
        message: "Pin Set",
        type: ELogTypes.STATE_CHANGED,
        data: { geoLocation, selectedMarkerData },
      }
    );
  };

export const updateSearchView =
  (activeView: "search" | "results" | "detail"): any =>
  (dispatch: AppDispatch) => {
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    dispatchLogger.fire(
      setSearchView,
      { activeView },
      {
        message: "Search Wizard View Changed",
        type: ELogTypes.STATE_CHANGED,
        data: { activeView },
      }
    );
  };

export default globalStateSlice.reducer;
